import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {SecondaryOfferList} from 'components/OfferList/SecondaryOfferList'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {useOtaMode} from 'hooks/useOtaMode'
import {
  getAccommodationPageHotelId,
  getHasActiveRoomFilter,
  getIsRoomsFiltersLoading,
  getMetaBrandHasRooms
} from 'modules/accommodation/selectors'
import {
  getAnchorPriceNightlyPerRoom,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {HotelOfferEntity} from 'modules/sapiSearch/slice'
import {getIsFilteredRoomsResultEmpty} from 'modules/searchApi/selectors'
import {RootState} from 'store'

import {Card} from '@vio/atlas/Card'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {cssTheme} from '@vio/atlas/themes'
import {BOFH_PROXY_PROVIDER_CODE} from '@vio/core/src/offer/business/isBofhOffer'
import {hexToRGBAString} from '@vio/core/src/utils/css'

import {OtaOffer, OtaOfferLoading} from '../OtaOffer'
import {PrimaryRoomOffer} from '../PrimaryRoomOffer'
import {PrimaryRoomOfferLoading} from '../PrimaryRoomOffer/PrimaryRoomOfferLoading'
import {AccommodationOfferBoxSoldout} from './AccommodationOfferBoxSoldout'

const Wrapper = styled.section`
  border-radius: ${cssTheme.layout.radius.xl};
  margin-bottom: 0;
  &.--border {
    border-width: 1.5px;
    border-style: solid;
    border-color: ${cssTheme.colors.border.neutral.secondary};
    overflow: hidden;
  }
  &.--primary.--border {
    border-color: ${cssTheme.colors.border.brand.tertiary};
    margin: -1px -1px 0 -1px;
    overflow: hidden;
    background: ${hexToRGBAString(
      cssTheme.colors.accommodationOfferBox.primary.background,
      0.5
    )};
  }
  &.--padding {
    padding-top: ${cssTheme.layout.spacing.s400};
    padding-bottom: ${cssTheme.layout.spacing.s500};
    padding-right: ${cssTheme.layout.spacing.s500};
    padding-left: ${cssTheme.layout.spacing.s500};
  }
  &.--primary {
    li:hover {
      background-color: ${cssTheme.colors.background.interactive.tertiaryHover};
    }
  }
`

interface MainProps {
  offerEntity?: HotelOfferEntity
  isLoading?: boolean
  showCashback?: boolean
}

const component = 'DesktopHotelViewTopOffers'
const OfferBoxComponent: React.FC<MainProps> = ({
  offerEntity,
  isLoading = false,
  showCashback
}) => {
  const hotelId = useSelector(getAccommodationPageHotelId)
  const anchorPrice = useSelector((state: RootState) =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const {topOffers, anchorPriceTotal} = useOfferContainer({
    offerEntity,
    hotelId
  })
  const secondaryOffers = topOffers
    .filter(offer => offer.intermediaryProvider !== BOFH_PROXY_PROVIDER_CODE)
    .slice(0, 2)
  const {checkIn, checkOut} = useSelector(getSearchParameters) || {}
  const hasActiveFilters = useSelector(getHasActiveRoomFilter)
  const hasNoRooms = useSelector(getIsFilteredRoomsResultEmpty)
  const isRoomsFiltersLoading = useSelector(getIsRoomsFiltersLoading)
  const brandHasRooms = useSelector(getMetaBrandHasRooms)
  const {isOTAMode} = useOtaMode()

  // For Search Whitelabels (brands where bookOnBrand.enabled = false), don't consider rooms filters loading
  // since we're not using rooms anyway
  const shouldConsiderRoomsLoading = brandHasRooms && isRoomsFiltersLoading

  if (isLoading || shouldConsiderRoomsLoading)
    return (
      <Wrapper className={cx(!isOTAMode && '--border')}>
        <Wrapper
          data-id="loader"
          className={cx('--padding', '--primary', '--border')}
        >
          {isOTAMode ? <OtaOfferLoading /> : <PrimaryRoomOfferLoading />}
        </Wrapper>
        {!isOTAMode &&
          Array(2)
            .fill(0)
            .map((_, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider sideMargin="s500" />}
                <Box
                  padding="s500"
                  display="flex"
                  justifyContent="space-between"
                >
                  <div>
                    <SkeletonLoader width={139} />
                    <SkeletonLoader width={90} />
                  </div>
                  <div>
                    <SkeletonLoader width={56} align="right" />
                    <SkeletonLoader width={86} align="right" />
                  </div>
                </Box>
              </React.Fragment>
            ))}
      </Wrapper>
    )
  if (!secondaryOffers.length && hasNoRooms) {
    return (
      <Card borderColorPath="border.neutral.secondary" borderRadius="lg">
        <AccommodationOfferBoxSoldout
          checkIn={checkIn}
          checkOut={checkOut}
          hasActiveFilters={hasActiveFilters}
        />
      </Card>
    )
  }

  return (
    <Wrapper className={!!topOffers.length && !isOTAMode && '--border'}>
      <Wrapper
        className={cx(
          !hasNoRooms && '--border',
          '--primary',
          !hasNoRooms && '--padding'
        )}
      >
        {!hasNoRooms ? (
          isOTAMode ? (
            <OtaOffer hotelId={hotelId} component={component} />
          ) : (
            <PrimaryRoomOffer
              hotelId={hotelId}
              component={component}
              showCashback={showCashback}
            />
          )
        ) : (
          <AccommodationOfferBoxSoldout
            checkIn={checkIn}
            checkOut={checkOut}
            hasOtherOffers
            hasActiveFilters={hasActiveFilters}
          />
        )}
      </Wrapper>
      {!isOTAMode && (
        <Wrapper
          className={cx(hasNoRooms && '--border', hasNoRooms && '--primary')}
        >
          <SecondaryOfferList
            variant="vertical"
            anchorPrice={anchorPrice}
            anchorPriceTotal={anchorPriceTotal}
            hotelId={hotelId}
            secondaryOffers={secondaryOffers}
            sourceComponent={component}
            showPriceIndication
            showCashback={showCashback}
            cashbackVariant="short"
          />
        </Wrapper>
      )}
    </Wrapper>
  )
}

export const AccommodationOfferBox = React.memo(OfferBoxComponent)
